/**
 *
 * @Copyright 2025 UNLOCKIT DECENTRALIZATION, LDA
 *
 */

import React, { FunctionComponent, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import {
    TranslationContext,
    withTranslationContext,
} from '../../../controllers/TranslationContext';

import Button from '../../Button';
import { ButtonVariant } from '../../../../types/general';
import { ContractConfigOption } from '../../../../types/contracts';
import Modal from '../../Modal';
import { ReactComponent as SwapIcon } from '../../../../assets/images/swap.svg';
import { Tooltip } from '../../Tooltip';
import { WorkflowDocument } from '../../../../types/workflows';
import { withWorkflowContext, WorkflowContext } from '../../../controllers/WorkflowContext';

type OwnProps = TranslationContext & Pick<WorkflowContext, 'updateWorkflowDocument'> & {
    document: WorkflowDocument;
    onSuccessfulUpdate?: () => void;
}

const SwapDocumentOptionComponent: FunctionComponent<OwnProps> = (props: OwnProps) => {
    const {
        t,
        document,
        updateWorkflowDocument,
        onSuccessfulUpdate,
    } = props;

    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
  
    const onSwapButtonClick = () => {
        setIsOpenModal(true);
    };

    /**
     * Start contract swap/replacement flow
     *
     * @remarks
     * First function of contract replacement flow
     */
    const handleContractSwap = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileReplaced = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            onReplaceContract(e.target.files[0]);
        }
    };

    const onReplaceContract = async (fileToUpload: File) => {
        setIsUploading(true);

        const [, error] = await updateWorkflowDocument(String(document.transactionId), String(document.id), {
            name: document.name,
            documentTypeId: String(document.documentTypeId),
            expiresAt: document.expiresAt ?? undefined,
            file: fileToUpload,
        });
        if (error) {
            onFailure(error.errors?.[0]?.getMessageTranslated(t));
            return;
        }
        onSuccess();
    };

    const onSuccess = () => {
        setIsUploading(false);
        setIsOpenModal(false);
        if (onSuccessfulUpdate) {
            onSuccessfulUpdate();
        }
    };

    const onFailure = (errorMessage: string) => {
        setIsUploading(false);
        toast.error(errorMessage);
    };

    return (
        <>
            <Button
                variant={ButtonVariant.IconBtn}
                data-step={ContractConfigOption.SwapContract}
                extraClasses={classNames('primary',
                    { active: isOpenModal })
                }
                onClick={onSwapButtonClick}
                testId="swap-btn"
            >
                <Tooltip title={t('workflows.documents.replaceDocumentModal.title')} placement="left">
                    <SwapIcon className="stroked" />
                </Tooltip>
            </Button>
            <Modal
                open={isOpenModal}
                title={t('workflows.documents.replaceDocumentModal.title')}
                handleClose={() => setIsOpenModal(false)}
            >
                <div className="content-wrap">
                    <p className="warning-msg">{t('workflows.documents.replaceDocumentModal.warning')}</p>
                    {!isUploading && (
                        <Button
                            onClick={handleContractSwap}
                            variant={ButtonVariant.Curved}
                            extraClasses="primary"
                            testId="confirm-btn"
                        >
                            {t('workflows.documents.replaceDocumentModal.confirm')}
                        </Button>
                    )}
                    {isUploading
                        && <CircularProgress color="primary" />}
                    <input hidden type="file" onChange={handleFileReplaced} ref={fileInputRef} data-testid="upload-file" />
                </div>
            </Modal>
        </>
    );
};

export const SwapDocumentOption = withTranslationContext(withWorkflowContext(SwapDocumentOptionComponent));
